import { ReactElement, useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Banner from 'components/shared/Banner';
import useCurrentUser from '../../hooks/useCurrentUser';
import { toCurrency } from '../../utils/numbers';
import useAddCreditCard from '../../hooks/useAddCreditCard';
import PaymentView, { UI } from '../shared/PaymentView';
import useAsyncEffect from '../../hooks/useAsyncEffect';
import Braintree, { PaymentService } from '../../services/Braintree';
import { PaymentUpdateFunnel } from '../../config';
import BillingAddressView from '../shared/BillingAddressView';
import HeapAnalytics from '../../services/HeapAnalytics';
import { removeTrailingDecimals } from '../../utils/strings';

function PaymentMethod(): ReactElement {
  const {
    currentUser,
    reload: reloadUser,
  } = useCurrentUser();
  const {
    credit,
    coupon_message: couponMessage,
    coupon_tooltip_message: couponTooltipMessage,
    billing_address: billingAddress,
    active_coupon: activeCoupon,
  } = currentUser;
  const [isPaymentProcessing, setPaymentProcessing] = useState(false);
  const [ccNumber, setCCNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [error, setError] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const addCreditCard = useAddCreditCard();
  const [paymentSaved, setPaymentSaved] = useState(false);
  useAsyncEffect(async () => Braintree.initialize(), []);

  const onSuccess = async () => {
    await reloadUser();
    setSelectedProvider(null);
    setPaymentProcessing(false);
    setPaymentSaved(true);
  };

  const validPaymentInfo = () => {
    const cleanCC = ccNumber.replace(/\s+/g, '');
    const isValidCC = /^\d{15,16}$/.test(cleanCC);
    const isValidCVC = /^\d{3,4}$/.test(cvc);
    return isValidCC && isValidCVC;
  };

  useEffect(() => {
    if (paymentSaved) {
      const timer = setTimeout(() => {
        setPaymentSaved(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [paymentSaved]);

  return (
    <>
      <div className="d-flex row g-1" style={{ marginTop: '27px' }}>
        <div className="d-flex gap-4 col-lg-6 justify-content-center">
          <div className="c-card c-my-account__payment-method__card">
            <div className="c-card__content">
              <h2 className="c-my-account__payment-method__heading">Add New Payment Method</h2>
              <PaymentView
                ui={UI.TABS}
                ccNumber={ccNumber}
                expMonth={expMonth}
                expYear={expYear}
                cvc={cvc}
                error={error}
                // @ts-expect-error TODO: fix null safety
                selectedProvider={selectedProvider}
                setCCNumber={setCCNumber}
                setExpMonth={setExpMonth}
                setExpYear={setExpYear}
                setCvc={setCvc}
                // @ts-expect-error TODO: fix null safety
                setSelectedProvider={setSelectedProvider}
                onAdd={() => {
                  setPaymentProcessing(true);
                }}
                onSuccess={() => {
                  reloadUser();
                  setSelectedProvider(null);
                  setPaymentProcessing(false);
                }}
              />
              <div
                id="payment-method-cc-submit"
                className="mb-0 c-my-account__payment-method__button-container"
              >
                <div className="c-signup-checkout__place-order">
                  <button
                    type="button"
                    className="c-button c-button--primary p-3"
                    disabled={isPaymentProcessing
                      || !validPaymentInfo()}
                    onClick={() => {
                      addCreditCard(
                        ccNumber,
                        cvc,
                        expMonth,
                        expYear,
                        setError,
                        setPaymentProcessing,
                        () => {
                          HeapAnalytics.trackSuccessfullyAuthPayment(
                            PaymentUpdateFunnel.USER_PROFILE,
                            PaymentService.CREDIT_CARD,
                          );
                          onSuccess();
                        },
                        billingAddress,
                      );
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
              { paymentSaved
                    && (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <div>
                        <Banner.Success isCentered>
                          <div className="u-text--center">
                            Payment updates saved!
                          </div>
                        </Banner.Success>
                      </div>
                    </div>
                    )}
            </div>
          </div>
        </div>
        <div className="d-flex gap-4 col-lg-6 justify-content-center">
          <div>
            <div className="c-card c-my-account__payment-method__card">
              <div className="c-card__content">
                <h2 className="c-my-account__payment-method__heading">Credits & Discounts</h2>
                <h3 className="c-my-account__payment-method__label">Remaining Credits</h3>
                <span>{toCurrency(credit)}</span>
                <br />
                <h3 className="c-my-account__payment-method__label">Applied Discounts</h3>
                {activeCoupon ? (
                  <span>
                    {couponMessage}
                    <OverlayTrigger
                      placement="bottom"
                      overlay={(
                        <Tooltip id="applied-discounts-tooltip">
                          {removeTrailingDecimals(couponTooltipMessage)}
                        </Tooltip>
                      )}
                    >
                      <button
                        className="c-link c-info-icon c-info-symbol"
                        type="button"
                        tabIndex={0}
                      >
                        &#9432;
                      </button>
                    </OverlayTrigger>
                  </span>
                ) : (<>$0.00</>)}
              </div>
            </div>
            <div className="c-card c-my-account__payment-method__card">
              <div className="c-card__content">
                <h2 className="c-my-account__payment-method__heading">Billing Address</h2>
                <BillingAddressView
                  address={billingAddress}
                  userID={currentUser?.id}
                  submitButtonLabel="Save Changes"
                  compactDesign
                  onSuccess={() => {
                    reloadUser();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PaymentMethod;
